import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { useLogout } from '@/hooks/useLogout';
import { doctorsApi, samplesApi, getErrorMessage, type ApiUser, type CompletedSample, type DoctorAssignment, type Sample } from '@/lib/api';
import { useSamples } from '@/hooks/useSamples';
import { PageHeader } from '@/components/PageHeader';
import { NotificationBell } from '@/components/NotificationBell';
import { StatsCard } from '@/components/StatsCard';
import { SearchInput } from '@/components/SearchInput';
import { PaginatedListFooter, DASHBOARD_PAGE_SIZE } from '@/components/PaginatedListFooter';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Users, UserPlus, Edit, Trash2, Shield, Stethoscope, Upload, Eye, ChevronLeft, Loader2, CheckCircle2, ChevronDown, Download,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function parsePaginated<T>(data: { results?: T[]; count?: number } | T[]): { items: T[]; total: number } {
  if (Array.isArray(data)) return { items: data, total: data.length };
  return { items: data.results ?? [], total: data.count ?? 0 };
}

function unwrapDoctors(data: { results?: ApiUser[] } | ApiUser[]): ApiUser[] {
  if (Array.isArray(data)) return data;
  return data.results ?? [];
}

function isBulkDownloadReady(sample: Pick<Sample, 'status' | 'has_conflicts'>): boolean {
  return sample.status === 'completed' || (sample.status === 'reconciling' && sample.has_conflicts === false);
}

type SelectedSampleMeta = Pick<Sample, 'sample_name' | 'status' | 'has_conflicts'>;

function metaFromSample(sample: Sample): SelectedSampleMeta {
  return {
    sample_name: sample.sample_name,
    status: sample.status,
    has_conflicts: sample.has_conflicts,
  };
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const handleLogout = useLogout();
  const [doctors, setDoctors] = useState<ApiUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const {
    samples,
    total,
    inReviewCount,
    completedCount,
    loading: samplesLoading,
    refresh: refreshSamples,
    pageSize,
  } = useSamples(searchQuery || undefined, page);

  const [doctorsModalOpen, setDoctorsModalOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<ApiUser | null>(null);
  const [doctorAssignments, setDoctorAssignments] = useState<DoctorAssignment[]>([]);
  const [doctorAssignedCount, setDoctorAssignedCount] = useState(0);
  const [doctorSubmittedCount, setDoctorSubmittedCount] = useState(0);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [selectedSamples, setSelectedSamples] = useState<Map<string, SelectedSampleMeta>>(new Map());
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [assignMenuOpen, setAssignMenuOpen] = useState(false);
  const [assignNoteOpen, setAssignNoteOpen] = useState(false);
  const [assignDoctorIds, setAssignDoctorIds] = useState<string[]>([]);
  const [assignNote, setAssignNote] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [pendingDownloadFormat, setPendingDownloadFormat] = useState<'xml' | 'json' | 'npy' | null>(null);
  const [downloading, setDownloading] = useState(false);

  const [completedModalOpen, setCompletedModalOpen] = useState(false);
  const [completedSearch, setCompletedSearch] = useState('');
  const [completedPage, setCompletedPage] = useState(1);
  const [completedItems, setCompletedItems] = useState<CompletedSample[]>([]);
  const [completedTotal, setCompletedTotal] = useState(0);
  const [completedLoading, setCompletedLoading] = useState(false);

  useEffect(() => {
    setPage(1);
    setSelectedSamples(new Map());
  }, [searchQuery]);

  // If this page is empty (deleted last page, or DRF 404), step back to a page that exists.
  useEffect(() => {
    if (samplesLoading) return;
    if (page > 1 && samples.length === 0) {
      setPage((p) => Math.max(1, p - 1));
      return;
    }
    if (total > 0) {
      const last = Math.max(1, Math.ceil(total / pageSize));
      if (page > last) setPage(last);
    }
  }, [samplesLoading, samples.length, page, total, pageSize]);

  // Keep status snapshots in sync for rows that are still selected and visible.
  useEffect(() => {
    setSelectedSamples((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Map(prev);
      for (const sample of samples) {
        const current = next.get(sample.id);
        if (!current) continue;
        const updated = metaFromSample(sample);
        if (
          current.sample_name !== updated.sample_name
          || current.status !== updated.status
          || current.has_conflicts !== updated.has_conflicts
        ) {
          next.set(sample.id, updated);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [samples]);

  const selectedSampleIds = useMemo(() => new Set(selectedSamples.keys()), [selectedSamples]);
  const allVisibleSelected = samples.length > 0 && samples.every((s) => selectedSampleIds.has(s.id));
  const someVisibleSelected = samples.some((s) => selectedSampleIds.has(s.id));
  const someSelected = selectedSamples.size > 0;

  const selectedNames = useMemo(
    () => [...selectedSamples.entries()].map(([id, meta]) => ({ id, name: meta.sample_name })),
    [selectedSamples],
  );

  const canBulkAssign = useMemo(() => {
    if (selectedSamples.size === 0) return false;
    return [...selectedSamples.values()].every((s) => s.status === 'unassigned');
  }, [selectedSamples]);

  const canBulkDownload = useMemo(() => {
    if (selectedSamples.size === 0) return false;
    return [...selectedSamples.values()].every(isBulkDownloadReady);
  }, [selectedSamples]);

  const activeDoctors = useMemo(
    () => doctors.filter((d) => d.is_active),
    [doctors],
  );

  const toggleSample = (sample: Sample, checked: boolean) => {
    setSelectedSamples((prev) => {
      const next = new Map(prev);
      if (checked) next.set(sample.id, metaFromSample(sample));
      else next.delete(sample.id);
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedSamples((prev) => {
      const next = new Map(prev);
      if (checked) {
        for (const sample of samples) next.set(sample.id, metaFromSample(sample));
      } else {
        for (const sample of samples) next.delete(sample.id);
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (!selectedSamples.size) return;
    setDeleting(true);
    try {
      const ids = [...selectedSamples.keys()];
      const { data } = await samplesApi.bulkDelete(ids);
      toast.success(data.detail || `Deleted ${data.deleted} sample(s)`);
      const remaining = Math.max(0, total - (data.deleted ?? ids.length));
      const last = Math.max(1, Math.ceil(remaining / pageSize) || 1);
      setSelectedSamples(new Map());
      setConfirmDeleteOpen(false);
      if (page > last) setPage(last);
      else void refreshSamples();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const toggleAssignDoctor = (id: string, checked: boolean) => {
    setAssignDoctorIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((d) => d !== id);
    });
  };

  const openAssignNoteDialog = () => {
    if (!assignDoctorIds.length) return;
    setAssignMenuOpen(false);
    setAssignNote('');
    setAssignNoteOpen(true);
  };

  const handleBulkAssign = async () => {
    if (!selectedSamples.size || !assignDoctorIds.length) return;
    setAssigning(true);
    try {
      const { data } = await samplesApi.bulkAssign({
        sample_ids: [...selectedSamples.keys()],
        doctor_ids: assignDoctorIds,
        admin_note: assignNote.trim() || undefined,
      });
      toast.success(data.detail || `Assigned ${data.assigned_samples} sample(s)`);
      setAssignNoteOpen(false);
      setAssignDoctorIds([]);
      setAssignNote('');
      setSelectedSamples(new Map());
      void refreshSamples();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setAssigning(false);
    }
  };

  const openDownloadDialog = (fmt: 'xml' | 'json' | 'npy') => {
    setDownloadMenuOpen(false);
    setPendingDownloadFormat(fmt);
    setDownloadDialogOpen(true);
  };

  const handleBulkDownload = async (includeImages: boolean) => {
    if (!selectedSamples.size || !pendingDownloadFormat) return;
    setDownloading(true);
    try {
      const { data } = await samplesApi.bulkExport({
        sample_ids: [...selectedSamples.keys()],
        fmt: pendingDownloadFormat,
        include_images: includeImages,
      });
      const blob = data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'samples_export.zip';
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selectedSamples.size} sample(s)`);
      setDownloadDialogOpen(false);
      setPendingDownloadFormat(null);
      setSelectedSamples(new Map());
      void refreshSamples();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  const fetchDoctors = useCallback(() => {
    doctorsApi.list().then(({ data }) => setDoctors(unwrapDoctors(data))).catch(() => toast.error('Failed to load doctors'));
  }, []);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const openDoctorsModal = () => {
    setSelectedDoctor(null);
    setDoctorAssignments([]);
    setDoctorAssignedCount(0);
    setDoctorSubmittedCount(0);
    setDoctorsModalOpen(true);
  };

  const openCompletedModal = () => {
    setCompletedSearch('');
    setCompletedPage(1);
    setCompletedModalOpen(true);
  };

  useEffect(() => {
    if (!completedModalOpen) return;
    setCompletedPage(1);
  }, [completedSearch, completedModalOpen]);

  useEffect(() => {
    if (!completedModalOpen) return;
    let cancelled = false;
    const load = async () => {
      setCompletedLoading(true);
      try {
        const params: Record<string, string> = {
          page: String(completedPage),
          page_size: String(DASHBOARD_PAGE_SIZE),
        };
        if (completedSearch.trim()) params.search = completedSearch.trim();
        const { data } = await samplesApi.completed(params);
        if (cancelled) return;
        const parsed = parsePaginated<CompletedSample>(data);
        setCompletedItems(parsed.items);
        setCompletedTotal(parsed.total);
      } catch {
        if (!cancelled) {
          toast.error('Failed to load completed samples');
          setCompletedItems([]);
          setCompletedTotal(0);
        }
      } finally {
        if (!cancelled) setCompletedLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [completedModalOpen, completedPage, completedSearch]);

  const handleDoctorSelect = async (doctor: ApiUser) => {
    setSelectedDoctor(doctor);
    setAssignmentsLoading(true);
    try {
      const { data } = await doctorsApi.assignments(doctor.id);
      setDoctorAssignments(data.assignments);
      setDoctorAssignedCount(data.assigned_count);
      setDoctorSubmittedCount(data.submitted_count);
    } catch {
      toast.error('Failed to load doctor assignments');
      setDoctorAssignments([]);
      setDoctorAssignedCount(0);
      setDoctorSubmittedCount(0);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const pendingReviews = inReviewCount;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Admin Dashboard"
        subtitle="Samples, doctors & export"
        icon={<Shield className="size-6 text-purple-600" />}
        iconBgClass="bg-purple-100"
        actions={<NotificationBell />}
        userName={user?.name}
        userRole="Administrator"
        onLogout={handleLogout}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <StatsCard title="Samples" value={total} icon={<Upload className="size-4 text-green-600" />} description="Uploaded batches" />
          <StatsCard
            title="Doctors"
            value={doctors.length}
            icon={<Stethoscope className="size-4 text-blue-600" />}
            description="Active validators"
            onClick={openDoctorsModal}
          />
          <StatsCard title="In review" value={pendingReviews} icon={<Users className="size-4 text-amber-600" />} description="Awaiting final selection" />
          <StatsCard
            title="Completed"
            value={completedCount}
            icon={<CheckCircle2 className="size-4 text-emerald-600" />}
            description="Admin finalized"
            onClick={openCompletedModal}
          />
        </div>

        <div className="mb-6">
          <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="Search by sample name…" />
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <CardTitle>Samples</CardTitle>
                <CardDescription>Upload batches, assign doctors, export final annotations</CardDescription>
              </div>
              <div className="flex gap-2">
                {canBulkDownload && (
                  <DropdownMenu open={downloadMenuOpen} onOpenChange={setDownloadMenuOpen}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline">
                        <Download className="size-4 mr-2" />
                        Download ({selectedSampleIds.size})
                        <ChevronDown className="size-4 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuLabel>Export format</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => openDownloadDialog('xml')}>XML</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => openDownloadDialog('json')}>JSON</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => openDownloadDialog('npy')}>NPY</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {canBulkAssign && (
                  <DropdownMenu open={assignMenuOpen} onOpenChange={setAssignMenuOpen}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline">
                        <UserPlus className="size-4 mr-2" />
                        Assign ({selectedSampleIds.size})
                        <ChevronDown className="size-4 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72">
                      <DropdownMenuLabel>Select doctors</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {activeDoctors.length === 0 ? (
                        <p className="px-2 py-3 text-sm text-muted-foreground">No doctors available</p>
                      ) : (
                        <div className="max-h-56 overflow-y-auto">
                          {activeDoctors.map((doc) => (
                            <DropdownMenuCheckboxItem
                              key={doc.id}
                              checked={assignDoctorIds.includes(doc.id)}
                              onCheckedChange={(checked) => toggleAssignDoctor(doc.id, checked === true)}
                              onSelect={(e) => e.preventDefault()}
                            >
                              <span className="truncate">{doc.full_name} ({doc.email})</span>
                            </DropdownMenuCheckboxItem>
                          ))}
                        </div>
                      )}
                      <DropdownMenuSeparator />
                      <div className="p-2">
                        <Button
                          className="w-full"
                          size="sm"
                          disabled={!assignDoctorIds.length}
                          onClick={openAssignNoteDialog}
                        >
                          Assign
                        </Button>
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {someSelected && (
                  <Button
                    variant="destructive"
                    onClick={() => setConfirmDeleteOpen(true)}
                  >
                    <Trash2 className="size-4 mr-2" />
                    Delete ({selectedSampleIds.size})
                  </Button>
                )}
                <Button variant="outline" onClick={() => navigate('/admin/upload')}>
                  <Upload className="size-4 mr-2" />
                  Upload
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allVisibleSelected ? true : someVisibleSelected ? 'indeterminate' : false}
                        onCheckedChange={(v) => toggleSelectAll(v === true)}
                        aria-label="Select all samples on this page"
                        disabled={samples.length === 0 || samplesLoading}
                      />
                    </TableHead>
                    <TableHead>Sample</TableHead>
                    <TableHead>Patches</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {samplesLoading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
                  ) : samples.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        {page > 1
                          ? 'This page is empty — going back to the previous page…'
                          : total === 0
                            ? 'No samples yet — upload a batch to get started'
                            : 'No matching samples'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    samples.map((sample) => (
                      <TableRow key={sample.id} data-state={selectedSampleIds.has(sample.id) ? 'selected' : undefined}>
                        <TableCell>
                          <Checkbox
                            checked={selectedSampleIds.has(sample.id)}
                            onCheckedChange={(v) => toggleSample(sample, v === true)}
                            aria-label={`Select ${sample.sample_name}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{sample.sample_name}</TableCell>
                        <TableCell>{sample.total_patches}</TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-gray-500">
                          {format(new Date(sample.upload_date), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell><StatusBadge status={sample.status} variant="admin"
                          label={
                            sample.status === 'reconciling' && sample.has_conflicts === false
                              ? 'Ready to download'
                              : undefined
                          }
                          className={
                            sample.status === 'reconciling' && sample.has_conflicts === false
                              ? 'bg-green-100 text-green-800'
                              : undefined
                          }
                        /></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/samples/${sample.id}`)}>
                            <Eye className="size-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <PaginatedListFooter
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
            />
            {someSelected && (
              <p className="text-xs text-muted-foreground mt-2">
                {selectedSamples.size} selected
                {selectedSamples.size !== samples.filter((s) => selectedSampleIds.has(s.id)).length
                  ? ' (includes other pages)'
                  : pageSize < total
                    ? ' on this page'
                    : ''}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <CardTitle>Doctors</CardTitle>
                <CardDescription>Register and manage pathologist accounts</CardDescription>
              </div>
              <Button onClick={() => navigate('/admin/doctors/new')}>
                <UserPlus className="size-4 mr-2" />
                Register doctor
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {doctors.length === 0 ? (
              <div className="border border-dashed rounded-lg p-8 text-center text-sm text-gray-500">No doctors registered yet</div>
            ) : (
              <div className="space-y-2">
                {doctors.map((doctor) => (
                  <div key={doctor.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border rounded-lg hover:bg-gray-50 gap-3">
                    <div>
                      <p className="font-medium">
                        {doctor.full_name}
                        {!doctor.is_active && (
                          <Badge variant="outline" className="text-xs ml-2 text-amber-700 border-amber-300">
                            Inactive
                          </Badge>
                        )}
                      </p>
                      <p className="text-sm text-gray-500">{doctor.email}</p>
                      {doctor.specialization && <Badge variant="outline" className="text-xs mt-1">{doctor.specialization}</Badge>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/doctors/${doctor.id}/edit`)}>
                        <Edit className="size-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={async () => {
                        if (!window.confirm(
                          `Permanently delete ${doctor.full_name}? This frees their email and removes their completed/assignment history.`,
                        )) {
                          return;
                        }
                        try {
                          await doctorsApi.delete(doctor.id);
                          fetchDoctors();
                          refreshSamples();
                          toast.success('Doctor deleted');
                        } catch {
                          toast.error('Failed to delete doctor');
                        }
                      }}>
                        <Trash2 className="size-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <Dialog open={doctorsModalOpen} onOpenChange={(open) => {
        setDoctorsModalOpen(open);
        if (!open) {
          setSelectedDoctor(null);
          setDoctorAssignments([]);
          setDoctorAssignedCount(0);
          setDoctorSubmittedCount(0);
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedDoctor ? selectedDoctor.full_name : 'Doctors'}
            </DialogTitle>
            <DialogDescription>
              {selectedDoctor
                ? 'Assigned batches and review status'
                : 'Active validators — click a doctor for assignment details'}
            </DialogDescription>
          </DialogHeader>

          {selectedDoctor ? (
            <div className="space-y-3">
              <Button
                variant="ghost"
                size="sm"
                className="px-0 text-gray-600"
                onClick={() => {
                  setSelectedDoctor(null);
                  setDoctorAssignments([]);
                  setDoctorAssignedCount(0);
                  setDoctorSubmittedCount(0);
                }}
              >
                <ChevronLeft className="size-4 mr-1" />
                Back to doctors
              </Button>

              <div className="text-sm text-gray-500">
                {selectedDoctor.email}
                {selectedDoctor.specialization && (
                  <Badge variant="outline" className="text-xs ml-2">{selectedDoctor.specialization}</Badge>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-2xl font-semibold">{doctorAssignedCount}</p>
                  <p className="text-xs text-gray-500">Samples assigned</p>
                </div>
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-2xl font-semibold">{doctorSubmittedCount}</p>
                  <p className="text-xs text-gray-500">Samples submitted</p>
                </div>
              </div>

              {assignmentsLoading ? (
                <p className="text-sm text-gray-500 py-6 text-center">Loading assignments…</p>
              ) : doctorAssignments.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center border border-dashed rounded-lg">
                  No batches assigned yet
                </p>
              ) : (
                <div className="space-y-2">
                  {doctorAssignments.map((assignment) => (
                    <div
                      key={assignment.id}
                      className="flex items-center justify-between gap-3 p-3 border rounded-lg"
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">{assignment.sample_name}</p>
                        <p className="text-xs text-gray-500">
                          {assignment.total_patches} patches · assigned{' '}
                          {format(new Date(assignment.assigned_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <StatusBadge status={assignment.status} variant="admin" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : doctors.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No doctors registered yet</p>
          ) : (
            <div className="space-y-2">
              {doctors.map((doctor) => (
                <button
                  key={doctor.id}
                  type="button"
                  className="w-full text-left flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                  onClick={() => handleDoctorSelect(doctor)}
                >
                  <div>
                    <p className="font-medium">{doctor.full_name}</p>
                    <p className="text-sm text-gray-500">{doctor.email}</p>
                  </div>
                  {typeof doctor.active_assignments === 'number' && doctor.active_assignments > 0 && (
                    <Badge variant="secondary" className="text-xs shrink-0">
                      {doctor.active_assignments} active
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={completedModalOpen}
        onOpenChange={(open) => {
          setCompletedModalOpen(open);
          if (!open) {
            setCompletedSearch('');
            setCompletedPage(1);
            setCompletedItems([]);
            setCompletedTotal(0);
          }
        }}
      >
        <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Completed</DialogTitle>
            <DialogDescription>
              Finalized samples and the doctors who submitted — kept after sample delete;
              removed when those doctors are deleted
            </DialogDescription>
          </DialogHeader>

          <SearchInput
            value={completedSearch}
            onChange={setCompletedSearch}
            placeholder="Search by sample name…"
          />

          {completedLoading ? (
            <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
          ) : completedItems.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center border border-dashed rounded-lg">
              No completed samples yet
            </p>
          ) : (
            <div className="space-y-2">
              {completedItems.map((sample) => (
                <div key={sample.id} className="p-3 border rounded-lg space-y-1.5">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-medium truncate">{sample.sample_name}</p>
                    <div className="flex items-center gap-1 shrink-0">
                      {sample.sample_deleted && (
                        <Badge variant="outline" className="text-xs">Deleted</Badge>
                      )}
                      <Badge variant="secondary" className="text-xs">
                        {sample.total_patches} patches
                      </Badge>
                    </div>
                  </div>
                  {sample.doctors.length === 0 ? (
                    <p className="text-xs text-gray-500">No submitted doctors on record</p>
                  ) : (
                    <p className="text-sm text-gray-600">
                      {sample.doctors.map((d) => d.full_name || d.email).join(', ')}
                    </p>
                  )}
                  {sample.reconciled_at && (
                    <p className="text-xs text-gray-400">
                      Finalized {format(new Date(sample.reconciled_at), 'MMM d, yyyy')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          <PaginatedListFooter
            page={completedPage}
            pageSize={DASHBOARD_PAGE_SIZE}
            total={completedTotal}
            onPageChange={setCompletedPage}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedSampleIds.size} sample{selectedSampleIds.size === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  This permanently deletes the selected case(s) from the database and removes
                  their patch images from server storage (MinIO). Assignments, annotations,
                  reviews, and related notifications are also removed. This cannot be undone.
                </p>
                {selectedNames.length > 0 && (
                  <ul className="list-disc pl-5 max-h-40 overflow-y-auto">
                    {selectedNames.map((item) => (
                      <li key={item.id}>{item.name}</li>
                    ))}
                  </ul>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={(e) => {
                e.preventDefault();
                void handleBulkDelete();
              }}
            >
              {deleting ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Trash2 className="size-4 mr-2" />}
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={assignNoteOpen} onOpenChange={setAssignNoteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Assign {assignDoctorIds.length} doctor{assignDoctorIds.length === 1 ? '' : 's'} to{' '}
              {selectedSampleIds.size} sample{selectedSampleIds.size === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>An optional note is sent with each assignment.</p>
                {selectedNames.length > 0 && (
                  <ul className="list-disc pl-5 max-h-32 overflow-y-auto">
                    {selectedNames.map((item) => (
                      <li key={item.id}>{item.name}</li>
                    ))}
                  </ul>
                )}
                <div className="space-y-2">
                  <Label htmlFor="bulk-assign-note">Admin note (optional)</Label>
                  <Textarea
                    id="bulk-assign-note"
                    value={assignNote}
                    onChange={(e) => setAssignNote(e.target.value)}
                    placeholder="Notes for the reviewing doctor…"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={assigning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={assigning}
              onClick={(e) => {
                e.preventDefault();
                void handleBulkAssign();
              }}
            >
              {assigning ? <Loader2 className="size-4 mr-2 animate-spin" /> : <UserPlus className="size-4 mr-2" />}
              Confirm assign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={downloadDialogOpen} onOpenChange={setDownloadDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Download {pendingDownloadFormat?.toUpperCase()} for {selectedSampleIds.size} sample
              {selectedSampleIds.size === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  {pendingDownloadFormat === 'npy'
                    ? 'NPY export is a ZIP with a folder per sample (one *_seg.npy per image). Optionally include the raw patch images in each folder.'
                    : 'Each sample is a separate folder in the ZIP. Optionally include raw unannotated patch images in each folder.'}
                </p>
                {selectedNames.length > 0 && (
                  <ul className="list-disc pl-5 max-h-32 overflow-y-auto">
                    {selectedNames.map((item) => (
                      <li key={item.id}>{item.name}</li>
                    ))}
                  </ul>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={downloading}
              onClick={(e) => {
                e.preventDefault();
                void handleBulkDownload(false);
              }}
            >
              Annotations only
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={downloading || !pendingDownloadFormat}
              onClick={(e) => {
                e.preventDefault();
                void handleBulkDownload(true);
              }}
            >
              {downloading ? <Loader2 className="size-4 mr-1 animate-spin" /> : null}
              Include raw images (ZIP)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
